import { Alert, Avatar, Button, Chip, DialogActions, DialogContent, Divider, Skeleton, Tooltip } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { TbBuilding, TbFileTypePdf, TbPlane, TbPlaneArrival, TbPlaneDeparture, TbTicket, TbUser } from "react-icons/tb";

import ClipBoard from "@/components/ClipBoard";
import { Image } from "@/components/Image";
import PassengerStatus from "@/components/PassengerStatus";
import { HotelNameColumnCell } from "@/components/table/HotelNameColumn";
import { airpots } from "@/constants/airpots";
import { formatFlightNumber } from "@/helpers/strings";
import { useGetPatient } from "@/requests/transport.queries";
import { OperationServiceFlightInfo } from "@/types/patient";
import { IPlanPassenger } from "@/types/tasks.types";

const PassengerInfoPopup = ({
    closePopup,
    passenger,
    raw,
}: {
    raw: boolean;
    passenger: Pick<IPlanPassenger<"api">, "status" | "id">;
    closePopup: () => void;
}) => {
    const { t } = useTranslation();
    const { data, isLoading, error } = useGetPatient({ id: passenger.id, raw });
    if (error) {
        closePopup();
    }

    return (
        <>
            <DialogContent dividers>
                {isLoading || !!error ? <PassengerLoading /> : <PassengerInfo passenger={passenger} data={data} />}
            </DialogContent>
            <DialogActions>
                <Button autoFocus color="secondary" variant="outlined" onClick={closePopup}>
                    {t("buttons.close")}
                </Button>
            </DialogActions>
        </>
    );
};

function TicketFile({ fileUrl }: { fileUrl: string }) {
    const isPdf = fileUrl.toLowerCase().endsWith(".pdf");

    if (isPdf) {
        return (
            <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
            >
                <TbFileTypePdf className="w-12 h-12 text-red-600 mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600">Bileti Görüntüle</span>
                <span className="text-xs text-gray-500 mt-1">PDF Dosyası</span>
            </a>
        );
    }

    return <Image imageUrl={fileUrl} />;
}

const PassengerLoadingSection = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="flex flex-col gap-1">
            <h3 className="flex text-black  items-center gap-1 font-medium">
                <Skeleton animation="wave" className="w-4" />
                <Skeleton animation="wave" className="w-32" />
            </h3>
            <div className=" pl-[17px] ">{children}</div>
        </div>
    );
};

const PassengerLoading = () => {
    return (
        <div className="mb-12">
            <div className="flex gap-7 ">
                <Skeleton
                    animation="wave"
                    variant="rounded"
                    className="!w-28  !grow-0 !shrink-0  !aspect-square !h-28"
                />
                <div className=" grid grid-cols-3   content-around items-center  place-content-center gap-1    w-full grow">
                    {Array.from({ length: 5 }).map((_, index) => (
                        <div key={index} className={index === 1 ? "col-span-2" : ""}>
                            <Skeleton animation="wave" className="!h-[15px] w-16" />
                            <Skeleton animation="wave" className="!h-[30px] w-36" />
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex mt-4 text-gray-600  text-sm flex-col gap-5">
                <PassengerLoadingSection>
                    <Skeleton animation="wave" className="w-48" />
                </PassengerLoadingSection>
                <PassengerLoadingSection>
                    <div className=" pl-[17px] text-black ">
                        <div className="grid items-center gap-1 grid-cols-[6fr,4fr,4fr,4fr] w-full">
                            <div></div>
                            <div className=" text-gray-500 text-xs">
                                <Skeleton animation="wave" className="w-16" />
                            </div>
                            <div className=" text-gray-500 text-xs">
                                <Skeleton animation="wave" className="w-16" />
                            </div>
                            <div className=" text-gray-500 text-xs">
                                <Skeleton animation="wave" className="w-16" />
                            </div>
                            {Array.from({ length: 2 }).map((_, index) => (
                                <React.Fragment key={index}>
                                    <div className="text-gray-500 text-xs flex gap-1 items-center">
                                        <Skeleton animation="wave" className="w-4" />
                                        <Skeleton animation="wave" className="w-10" />
                                    </div>
                                    <span className=" -translate-x-1">
                                        <div className="flex items-center gap-2">
                                            <Skeleton animation="wave" className="!w-6 !h-10 !aspect-square" />
                                            <Skeleton animation="wave" className="w-10" />
                                        </div>
                                    </span>
                                    <span>
                                        <Skeleton animation="wave" className="w-12" />
                                    </span>
                                    <span>
                                        <Skeleton animation="wave" className="w-28" />
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </PassengerLoadingSection>

                <PassengerLoadingSection>
                    <div className="grid grid-cols-[1fr,5px,1fr] gap-3">
                        <Skeleton animation="wave" className="w-full !h-24 !-translate-y-0  rounded" />
                        <span></span>
                        <Skeleton animation="wave" className="w-full !h-24 !-translate-y-0 rounded" />
                    </div>
                </PassengerLoadingSection>
            </div>
        </div>
    );
};

const PassengerInfo = ({
    data,
    passenger,
}: {
    passenger: Pick<IPlanPassenger<"api">, "status" | "id">;
    data: OperationServiceFlightInfo | undefined;
}) => {
    const { t } = useTranslation();
    if (!data) return null;
    return (
        <div>
            <div className="flex  gap-7">
                <Avatar className="!w-28  !aspect-square !h-28 " variant="rounded">
                    <TbUser size={80} />
                </Avatar>
                <div className=" grid grid-cols-3   content-around items-center  place-content-center gap-1    w-full grow">
                    <div>
                        <span className="text-gray-500 text-xs  ">{t("name")}</span>
                        <div className=" font-medium  text-black !h-8 leading-none  !items-start">
                            <div>
                                <ClipBoard text={data.name || ""}>{data.name}</ClipBoard>
                            </div>
                        </div>
                    </div>
                    <div className=" col-span-1">
                        <span className="text-gray-500 text-xs  ">{t("companions")}</span>
                        <div className=" font-medium  text-black !h-8 leading-none">
                            {(data.companions &&
                                data.companions.length &&
                                data.companions.map((item) => item.name).join(" , ")) ||
                                "-"}
                        </div>
                    </div>
                    <div>
                        <span className="text-gray-500 text-xs  ">{t("accommodation")}</span>
                        <div className=" font-medium  text-black !h-8 leading-none -translate-y-[2px]">
                            {data.clinic_data?.stay?.hotel?.hotelName && (
                                <HotelNameColumnCell value={data.clinic_data?.stay?.hotel?.hotelName} />
                            )}
                        </div>
                    </div>

                    <div>
                        <span className="text-gray-500 text-xs  ">{t("klinik")}</span>
                        <div className=" font-medium  text-black !h-8 leading-none">{data.clinic} </div>
                    </div>
                    <div className="">
                        <span className="text-gray-500 text-xs   ">{t("languages")}</span>
                        <div className=" font-medium  text-black h-8 leading-none">{data.languages?.join("-")}</div>
                    </div>
                    <div className="    ">
                        {passenger.status && (
                            <>
                                <span className="text-gray-500 text-xs  ">{t("status")}</span>
                                <div className=" font-medium  text-black h-8 leading-none -translate-y-[2px]">
                                    <PassengerStatus status={passenger.status} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex mt-4 text-gray-600  text-sm flex-col gap-5">
                <div className="flex flex-col gap-1">
                    <h3 className="flex text-black  items-center gap-1 font-medium">
                        <TbBuilding /> {t("accommodationInfo")}
                    </h3>
                    <div className=" pl-[17px] ">
                        <div>{data.stay?.nigths}</div>
                        <div>{data.stay?.room_type}</div>
                        <div>{data.stay?.stay_in}</div>
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <h3 className="flex text-black  items-center gap-1 font-medium">
                        <TbPlane /> {t("flightInfo")}
                    </h3>
                    <div className=" pl-[17px] text-black ">
                        <div className="grid items-center gap-1 grid-cols-[6fr,4fr,4fr,4fr] w-full">
                            <div></div> <div className=" text-gray-500 text-xs">{t("airport")}</div>{" "}
                            <div className=" text-gray-500 text-xs">{t("flight-number")}</div>
                            <div className=" text-gray-500 text-xs">Tarih</div>
                            <div className="text-gray-500 text-xs flex gap-1 items-center">
                                <TbPlaneArrival size={14} />
                                Geliş
                            </div>
                            <AirportColumn
                                flightNumber={data.flight?.arrival_flight_number}
                                airport={data.flight?.arrival_airport}
                                flightTime={`${data.flight?.arrival_date || ""} ${data.flight?.arrival_time || ""}`}
                            />
                            <div className="text-gray-500 text-xs flex gap-1 items-center">
                                <TbPlaneDeparture size={14} />
                                Gidiş
                            </div>
                            <AirportColumn
                                flightNumber={data.flight?.departure_flight_number}
                                airport={data.flight?.departure_airport}
                                flightTime={`${data.flight?.departure_date || ""} ${data.flight?.departure_time || ""}`}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-1">
                    <h3 className="flex text-black  items-center gap-1 font-medium">
                        <TbTicket /> {t("ticketInfo")}
                    </h3>
                    <div className=" pl-[17px] ">
                        <div className="grid grid-cols-[1fr,5px,1fr] gap-4">
                            {data.flight?.tickets ? (
                                data.flight.tickets.map((fileUrl, index) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <TicketFile fileUrl={fileUrl} />
                                            {index % 2 === 0 && (
                                                <Divider
                                                    component="div"
                                                    orientation="vertical"
                                                    className="!my-auto !h-3/4"
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <Alert className="  mt-2 !w-full" variant="outlined" severity="warning">
                                    {t("ticketNotFound")}
                                </Alert>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AirportColumn = ({
    flightNumber,
    airport,
    flightTime,
}: {
    airport: string | undefined;
    flightTime: string | undefined;
    flightNumber: string | undefined;
}) => {
    const { t } = useTranslation();
    const targetAirport = airpots.find((item) => item.value === airport);
    return (
        <>
            <span className=" -translate-x-2">
                {targetAirport && (
                    <Tooltip title={t(`${airport}_desc`)}>
                        <Chip
                            sx={{
                                color: "black",
                                backgroundColor: "transparent",
                                fontWeight: 500,
                            }}
                            icon={<img src={targetAirport.img} className="w-6 h-6  object-contain"></img>}
                            label={targetAirport.value}
                        />
                    </Tooltip>
                )}
            </span>
            <span>
                <ClipBoard text={formatFlightNumber(flightNumber || "")}>
                    {formatFlightNumber(flightNumber || "")}
                </ClipBoard>
            </span>
            <span>{flightTime}</span>
        </>
    );
};
export default PassengerInfoPopup;
